<template>
   <ClientOnly>
      <div v-if="showSiteCookies">
         <CookiesManagerMini
            v-if="!state.showCustomiseModal"
            :description="cookieBannerContent"
            @customise-choices="showCustomiseModal(true)"
         />
         <CookiesCustomiseModal :dialogIsOpen="state.showCustomiseModal" @close="showCustomiseModal(false)" />
      </div>
   </ClientOnly>
</template>

<script setup lang="ts">
import { getCookieByName } from "@/js/helpers/cookies.utils";
import { useCookieSettingsStore } from "@/stores/cookieSettingsStore";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useBaseContentStore } from "@/stores/baseContentStore";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

// declarations
// -----------------------------------------
dayjs.extend(utc);

const baseContentStore = useBaseContentStore();
const cookieSettingsStore = useCookieSettingsStore();
const clientSettings = inject(useClientSettingsKey);
const cookiePolicyUpdatedOn: Dayjs = clientSettings.cookiesDateChanged; // UTC format 2024-04-15T21:46:13.236Z
const userCookieValue: string | null = getCookieByName(cookieSettingsStore.consentedOnCookieKey); // ex. 2024-04-15T21:46:13.236Z

// state
const state = reactive({
   userCookiesConsentedOn: userCookieValue ? dayjs(userCookieValue).utc() : null,
   showCustomiseModal: false,
});

// computed
// -----------------------------------------
const cookieBannerContent = computed(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.cookieBanner;
});

const showSiteCookies = computed(() => {
   // No cookie banner content > we dont show the cookie manager
   if (!cookieBannerContent.value) {
      return false;
   }
   // User has not consented to the cookie policy yet > reshow the cookie manager
   else if (!state.userCookiesConsentedOn) {
      return true;
   }
   // Date the user consented to the cookie policy is less than the date the cookie policy was updated >  reshow the cookie manager
   // Note that the cookie policy updated is never a futuristic value (ie when a new service is added, the date is always the current date)
   else if (cookiePolicyUpdatedOn.isAfter(state.userCookiesConsentedOn)) {
      return true;
   }
   // All conditions false, defaulting to not showing the cookie manager
   else {
      return false;
   }
});

// methods
// -----------------------------------------
function showCustomiseModal(show: boolean) {
   state.showCustomiseModal = show;
}
</script>
